import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Details, ValidationMessage } from '@digdir/designsystemet-react';
import type { QuestionnaireQuestion } from '../../../api/workEnvironmentTypes';
import { ArrayUtils } from '../../../utils/ArrayUtils';
import { questionElementId } from '../workEnvironmentFormView';
import classes from '../WorkEnvironmentFormSubmission.module.css';

type Props = {
  question: QuestionnaireQuestion;
  /** The words for 1–5, in the language the survey is shown in */
  labels: string[];
  number: number;
  total: number;
  value: number | undefined;
  onChange: (value: number) => void;
  invalid: boolean;
};

/** Every step has words, so «2» means the same to everyone */
export const QuestionCard = ({ question, labels, number, total, value, onChange, invalid }: Props): ReactElement => {
  const { t } = useTranslation();
  const label = (scale: number) => labels[scale - 1];
  const id = questionElementId(question.ref);

  return (
    <fieldset id={id} className={classes.question} data-invalid={invalid || undefined}>
      <legend>{question.text}</legend>
      <div className={classes.questionText}>
        <span className={`ds-paragraph ${classes.subtle}`} data-size="xs">
          {t('workEnvironmentFormSubmission.question', { current: number, total })}
        </span>
        <p className="ds-heading" data-size="xs" aria-hidden="true">
          {question.text}
        </p>
      </div>
      <div className={classes.scale}>
        <div className={classes.options}>
          {ArrayUtils.getLickertScaleUpTo(5).map((scale) => (
            <label key={scale} className={classes.option}>
              <input
                className="ds-input"
                type="radio"
                name={id}
                value={scale}
                aria-label={`${scale} – ${label(scale)}`}
                checked={value === scale}
                onChange={() => onChange(scale)}
              />
              <span className="ds-label" aria-hidden="true">
                {scale}
              </span>
              <span className={`ds-paragraph ${classes.optionText}`} data-size="xs" aria-hidden="true">
                {label(scale)}
              </span>
            </label>
          ))}
        </div>
        {invalid && <ValidationMessage>{t('workEnvironmentFormSubmission.answerError')}</ValidationMessage>}
      </div>
      {question.help && (
        <Details>
          <Details.Summary>{t('surveys.workEnvironmentSurveyForm.explanationTitle')}</Details.Summary>
          <Details.Content>{question.help}</Details.Content>
        </Details>
      )}
    </fieldset>
  );
};
